import { motion } from "framer-motion";
import { Stethoscope, Baby, Accessibility, Ear, Heart } from "lucide-react";
import { FaLungs, FaAllergies, FaWhatsapp } from "react-icons/fa";
import { openWhatsApp } from "@/lib/whatsapp";

const SPECIALISTS = [
  { icon: Stethoscope, color: "#5B2C91", name: "Medico generalista", desc: "Valutazione clinica generale, rilevazione dei parametri vitali e inquadramento dei disturbi non urgenti." },
  { icon: Baby, color: "#FF7EB3", name: "Pediatra", desc: "Valutazione pediatrica assistita di gola, orecchie, apparato respiratorio e principali parametri clinici." },
  { icon: Accessibility, color: "#0EA5E9", name: "Geriatra e internista", desc: "Valutazione multidimensionale del paziente adulto o anziano, con particolare attenzione a cronicità, fragilità e comorbidità." },
  { icon: Ear, color: "#FF8A5B", name: "Otorinolaringoiatra", desc: "Valutazione specialistica assistita di orecchie, naso e gola mediante immagini diagnostiche trasmesse in diretta." },
  { icon: FaLungs, color: "#10B981", name: "Pneumologo", desc: "Valutazione dell’apparato respiratorio con auscultazione polmonare e rilevazione dei principali parametri respiratori." },
  { icon: FaAllergies, color: "#8B5CF6", name: "Dermatologo", desc: "Valutazione specialistica di alterazioni cutanee attraverso immagini cliniche ad alta definizione trasmesse in diretta." },
  { icon: Heart, color: "#E5484D", name: "Cardiologo", desc: "Valutazione cardiologica assistita con auscultazione, rilevazione dei parametri ed eventuale ECG portatile." },
];

export default function SpecialistsSection() {
  const request = (name) =>
    openWhatsApp(
      `Buongiorno, vorrei ricevere maggiori informazioni sul servizio TytoCare, in particolare per ${name}.`
    );

  return (
    <section id="specialisti" className="bg-[#F6F4FA] py-20 sm:py-28" data-testid="specialists-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#5B2C91]"
          >
            <span className="h-2 w-2 rounded-full bg-[#D4F84A]" />
            Medici
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#1F152E]"
            data-testid="specialists-title"
          >
            Medici generalisti e specialisti, senza farli viaggiare da una casa all’altra
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-5 text-base sm:text-lg leading-relaxed text-[#4A4059]"
          >
            Il medico può trovarsi ovunque. È l’infermiere a portare gli strumenti diagnostici al
            paziente.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 overflow-hidden rounded-[2rem] lg:rounded-[2.5rem] shadow-[0_30px_80px_rgba(91,44,145,0.18)] ring-1 ring-white/60"
        >
          <img
            src="/generated/specialisti-team.png"
            alt="Team di medici generalisti e specialisti sorridenti che mostrano il dispositivo TytoCare"
            className="w-full h-[300px] sm:h-[420px] lg:h-[480px] object-cover object-center"
            data-testid="specialists-team-image"
          />
        </motion.div>

        <div className="mt-12 flex flex-wrap justify-center gap-5">
          {SPECIALISTS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.07, ease: [0.16, 1, 0.3, 1] }}
                className="flex w-full flex-col rounded-3xl bg-white p-6 ring-1 ring-[#5B2C91]/8 shadow-[0_8px_30px_rgb(91,44,145,0.05)] transition-transform duration-300 hover:-translate-y-1.5 sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.834rem)]"
                data-testid="specialist-card"
              >
                <span
                  className="inline-flex h-12 w-12 items-center justify-center rounded-2xl"
                  style={{ backgroundColor: `${s.color}18`, color: s.color }}
                >
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="font-heading mt-5 text-xl font-semibold text-[#1F152E]">{s.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#6B6280]">{s.desc}</p>
                <button
                  onClick={() => request(s.name)}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#5B2C91]/8 px-5 py-3 text-sm font-semibold text-[#5B2C91] hover:bg-[#5B2C91] hover:text-white transition-colors duration-200"
                  data-testid="specialist-cta"
                >
                  <FaWhatsapp className="h-4 w-4" />
                  Verifica disponibilità
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
